import { autorun } from "mobx";
import {
  BillingMethodType,
  InvoiceStatus,
  NewPaymentRelatedSource,
} from "../enums";
import {
  IAPICreateInvoiceModel,
  IAPIPayInvoiceModel,
  IAPIUpdateInvoiceModel,
} from "../interfaces";
import { RelatedPaymentsGridModel } from "./RelatedPaymentsGridModel";
import { MerchantAccount } from "./MerchantApplicationInputModel";
import { PaymentMethodModel } from "./PaymentMethodModels";
import {
  AuthService,
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
  nameof,
  TimeAMPM,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { ContactReferenceModel } from "./ContactDetailPageModel";
import { InvoiceDetailModel } from "./InvoiceDetailPageModel"; 

export class UpsertInvoiceModel {
  id: string;
  description: string;
  amount: string;
  dueDate: string;
  isDraft: boolean;
  payments: RelatedPaymentsGridModel[];
  billingMethod: keyof typeof BillingMethodType;
  paymentMethod?: PaymentMethodModel;
  merchantAccount?: MerchantAccount;
  errors: InputErrorModel<UpsertInvoiceModel>[];
  contact?: ContactReferenceModel;
  status: keyof typeof InvoiceStatus;
  //for update only
  totalRefundedAmount: number;
  totalCreditAmount: number;
  paidAmount: number;
  balance: number;
  discount: number;
  surcharge: number;
  balanceValue: string;
  //For pay popup
  amountToPay: string;
  isPayPopUp: boolean;
  time: string;
  amPm: keyof typeof TimeAMPM;
  source: keyof typeof NewPaymentRelatedSource;
  sourceId: string; //id of contact, subscription, invoice, checkout, purchaselinks
  isNewPaymentMethod: boolean;
  newPaymentGatewayMethodId: string;
  invoiceNumber: string;
  isNewPaymentMethodPrimary: boolean;
  isContactRequired: boolean;

  constructor(data?: Partial<UpsertInvoiceModel>) {
    this.id = data?.id || "";
    this.description = data?.description || "";
    this.amount = data?.amount || "";
    this.dueDate = data?.dueDate || UtilityMethods.toLocalISOString(new Date());
    this.isDraft = data?.isDraft || false;
    this.billingMethod = data?.billingMethod || "ChargePayment";
    this.paymentMethod =
      data?.paymentMethod && new PaymentMethodModel(data.paymentMethod);
    this.merchantAccount =
      data?.merchantAccount && new MerchantAccount(data.merchantAccount);
    this.contact = data?.contact && new ContactReferenceModel(data.contact);
    this.status = data?.status || "Open";
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<UpsertInvoiceModel>(e);
        })) ||
      [];
    this.totalRefundedAmount = data?.totalRefundedAmount || 0;
    this.totalCreditAmount = data?.totalCreditAmount || 0;
    this.paidAmount = data?.paidAmount || 0;
    this.balance = data?.balance || 0;
    this.discount = data?.discount || 0;
    this.surcharge = data?.surcharge || 0;
    this.balanceValue = data?.balanceValue || "";
    this.amountToPay = data?.amountToPay || "";
    this.isPayPopUp = data?.isPayPopUp || false;
    this.time = data?.time || "";
    this.amPm = data?.amPm || "AM";
    this.source = data?.source || "Manual";
    this.sourceId = data?.sourceId || "";
    this.invoiceNumber = data?.invoiceNumber || "";
    this.isNewPaymentMethod = data?.isNewPaymentMethod || false;
    this.newPaymentGatewayMethodId = data?.newPaymentGatewayMethodId || "";
    this.isNewPaymentMethodPrimary = data?.isNewPaymentMethodPrimary || false;
    this.isContactRequired = data?.isContactRequired !== false;

    this.payments = (
      data?.payments?.map((item) => new RelatedPaymentsGridModel(item)) || []
    ).filter((x) => x.success);
    if (!data) {
      this.time = UtilityMethods.getHHMMFromDate(new Date());
      this.amPm = UtilityMethods.getAMPMFromDate(new Date());
      this.dueDate = new Date().toISOString();
    }
    autorun(() => {
      this.validate(true);
    });
  }

  static createModelFromInvoiceDetail(
    data: InvoiceDetailModel,
    payments: RelatedPaymentsGridModel[],
    isPayPopUp = false
  ): UpsertInvoiceModel {
    return new UpsertInvoiceModel({
      id: data.id,
      description: data.description,
      amount: data.amount.toString(),
      dueDate: data.dueDate
        ? UtilityMethods.toLocalISOString(data.dueDate)
        : UtilityMethods.toLocalISOString(new Date()),
      paymentMethod: data.paymentMethod,
      merchantAccount: data.merchantAccount,
      billingMethod: data.billingMethod,
      contact: data.contact,
      status: data.status,
      totalRefundedAmount: data.totalRefundedAmount,
      totalCreditAmount: data.totalCreditAmount,
      paidAmount: data.paidAmount,
      balance: data.balance,
      balanceValue: data.balance.toString(),
      isPayPopUp: isPayPopUp,
      time: UtilityMethods.getHHMMFromDate(data.dueDate),
      amPm: UtilityMethods.getAMPMFromDate(data.dueDate),
      source: NewPaymentRelatedSource.Invoice,
      sourceId: data.id,
      invoiceNumber: data.invoiceNumber,
      payments: payments,
      discount: data.discount,
      surcharge: data.surcharge,
    });
  }

  hasError(field: keyof UpsertInvoiceModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<UpsertInvoiceModel>[] = [];
    CoreInputModelUtil.validateTextField(
      this.description,
      "description",
      newErrors
    );
    CoreInputModelUtil.validateDecimalValue(
      this.amount,
      "amount",
      newErrors,
      undefined,
      true
    );

    if (
      !(this.contact?.id || this.contact?.mindmeContactId) &&
      this.isContactRequired
    ) {
      CoreInputModelUtil.addError("contact", newErrors, "Contact is required");
    }
    if (!this.paymentMethod && !this.isNewPaymentMethod) {
      CoreInputModelUtil.addError(
        "paymentMethod",
        newErrors,
        "Payment method is required"
      );
    }

    if (!this.merchantAccount) {
      CoreInputModelUtil.addError(
        "merchantAccount",
        newErrors,
        "Merchant account is required"
      );
    }

    if (this.id) {
      CoreInputModelUtil.validateDecimalValue(
        this.balanceValue,
        "balanceValue",
        newErrors,
        undefined,
        true
      );

      if (this.balanceValue && parseFloat(this.balanceValue) > this.balance) {
        CoreInputModelUtil.addError(
          "balanceValue",
          newErrors,
          "Balance value cannot be greater than balance"
        );
      }

      if (!this.status) {
        CoreInputModelUtil.addError("status", newErrors, "Status is required");
      }
    }

    if (this.isPayPopUp) {
      CoreInputModelUtil.validateDecimalValue(
        this.amountToPay,
        "amountToPay",
        newErrors,
        undefined,
        true
      );
      if (this.amountToPay && parseFloat(this.amountToPay) > this.balance) {
        CoreInputModelUtil.addError(
          "amountToPay",
          newErrors,
          "Amount to pay cannot be greater than balance"
        );
      }
    } else {
      const isValidDate = CoreInputModelUtil.validateDateField(
        "dueDate",
        this.dueDate,
        newErrors
      );
      const isValidTime = CoreInputModelUtil.validateTime(
        this.time,
        "time",
        this.amPm,
        newErrors
      );
      if (isValidDate && isValidTime) {
        CoreInputModelUtil.validateDateAndTime(
          nameof<UpsertInvoiceModel>("dueDate"),
          nameof<UpsertInvoiceModel>("time"),
          new Date(this.dueDate),
          this.time,
          this.amPm,
          newErrors,
          0
        );
      }
    }

    if (this.isNewPaymentMethod && !this.merchantAccount?.isNMIMerchantAccount) {
      CoreInputModelUtil.validateTextField(
        this.newPaymentGatewayMethodId,
        "newPaymentGatewayMethodId",
        newErrors
      );
    }

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }

  createNewInvoiceAPIModel(
    contactId: string,
    mindmeContactId?: string
  ): IAPICreateInvoiceModel {
    return {
      accountId: AuthService.authData.accountId,
      contactId: contactId,
      description: this.description,
      amount: parseFloat(this.amount),
      dueDate: UtilityMethods.addTimeToDate(
        new Date(this.dueDate),
        this.time,
        this.amPm
      ),
      isDraft: this.isDraft,
      paymentMethodId: this.paymentMethod?.id || "",
      merchantAccount: this.merchantAccount?.createAPIModel2() || ({} as any),
      billingMethod: this.billingMethod,
      source: this.source,
      sourceId: this.sourceId,
      newPaymentGatewayMethodId: this.newPaymentGatewayMethodId,
      isNewPaymentMethodPrimary: this.isNewPaymentMethodPrimary,
      mindmeContactId: mindmeContactId || "",
    };
  }

  createUpdateInvoiceModel(contactId: string): IAPIUpdateInvoiceModel {
    return {
      id: this.id,
      accountId: AuthService.authData.accountId,
      contactId: contactId,
      description: this.description,
      amountDue: parseFloat(this.balanceValue),
      dueDate: UtilityMethods.addTimeToDate(
        new Date(this.dueDate),
        this.time,
        this.amPm
      ),
      status: this.status,
      source: this.source,
      sourceId: this.sourceId,
      newPaymentGatewayMethodId: this.newPaymentGatewayMethodId,
      paymentMethodId: this.newPaymentGatewayMethodId
        ? ""
        : this.paymentMethod?.id || "",
      isNewPaymentMethodPrimary: this.isNewPaymentMethodPrimary,
    };
  }

  createPayInvoiceModel(): IAPIPayInvoiceModel {
    return {
      id: this.id,
      accountId: AuthService.authData.accountId,
      amount: parseFloat(this.amountToPay),
      paymentMethodId: this.paymentMethod?.id || "",
      merchantAccount: this.merchantAccount?.createAPIModel() || ({} as any),
      newPaymentGatewayMethodId: this.newPaymentGatewayMethodId,
    };
  }
}
